import { Badge, Box, HStack, Icon, Stack, Table, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { LuChevronRight, LuCircleCheck } from 'react-icons/lu';
import type { CarteraContractRow } from '@/lib/api';
import { fmtMoneyWhole } from '@/utils/format';

const MAX_ROWS = 8;

function lotLabel(r: CarteraContractRow): string {
  const mz = r.manzana ? `Mz ${r.manzana}` : '';
  const lot = r.lotNumber ? `L${r.lotNumber}` : '';
  return [mz, lot].filter(Boolean).join(' ') || '—';
}

export function AttentionList({ rows }: { rows: CarteraContractRow[] }) {
  const overdue = rows
    .filter((r) => Number(r.overdueBalance) > 0)
    .sort((a, b) => Number(b.overdueBalance) - Number(a.overdueBalance))
    .slice(0, MAX_ROWS);

  return (
    <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '5' }}>
      <HStack justify="space-between" mb="3">
        <Text fontSize="sm" fontWeight="700">
          Requiere tu atención hoy
        </Text>
        {overdue.length > 0 && <Badge colorPalette="red">{overdue.length}</Badge>}
      </HStack>

      {overdue.length === 0 ? (
        <HStack gap="2" color="fg.muted" py="4">
          <Icon as={LuCircleCheck} color="green.500" boxSize="5" />
          <Text fontSize="sm">Sin cuotas vencidas — cartera al día.</Text>
        </HStack>
      ) : (
        <>
          <Table.Root size="sm" display={{ base: 'none', md: 'table' }}>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Cliente</Table.ColumnHeader>
                <Table.ColumnHeader>Lote</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">Vencido</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">Cuotas</Table.ColumnHeader>
                <Table.ColumnHeader />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {overdue.map((r) => (
                <Table.Row key={r.contractId}>
                  <Table.Cell>{r.buyerName}</Table.Cell>
                  <Table.Cell color="fg.muted">{lotLabel(r)}</Table.Cell>
                  <Table.Cell textAlign="end" fontWeight="600" color="red.600">
                    {fmtMoneyWhole(r.overdueBalance)}
                  </Table.Cell>
                  <Table.Cell textAlign="end">{r.overdueCount}</Table.Cell>
                  <Table.Cell textAlign="end">
                    <Box
                      as={RouterLink}
                      {...({ to: `/contratos/${r.contractId}` } as { to: string })}
                      color="brand.600"
                      fontWeight="600"
                      fontSize="sm"
                      display="inline-flex"
                      alignItems="center"
                    >
                      Ver contrato <Icon as={LuChevronRight} />
                    </Box>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
            {overdue.map((r) => (
              <Box
                key={r.contractId}
                as={RouterLink}
                {...({ to: `/contratos/${r.contractId}` } as { to: string })}
                borderWidth="1px"
                borderColor="border"
                rounded="md"
                p="3"
                textDecoration="none"
                _hover={{ bg: 'bg.subtle' }}
              >
                <HStack justify="space-between">
                  <Stack gap="0">
                    <Text fontWeight="600" fontSize="sm">
                      {r.buyerName}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {lotLabel(r)} · {r.overdueCount} cuota(s)
                    </Text>
                  </Stack>
                  <Text fontWeight="700" color="red.600" fontSize="sm">
                    {fmtMoneyWhole(r.overdueBalance)}
                  </Text>
                </HStack>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
